import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Row, Tooltip } from 'antd';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { aiCohortDrawerAtom, fullSensitivityConfigAtom, selectedProductAtom } from '../atoms';
import { ECO_AI_ALERT_NWR_METRICS } from '../../../constants';
import {
  StyledInput,
  StyledUnit,
  StyledConfigLabel,
  StyledInputWrapper,
  ImpactedFieldsWrapper,
  StyledDivider,
  StyledTitle,
} from './styled';
import { getMetricDisplayName, getMetricUnitByKey } from '@src/utils/metrics';
import { useSelector } from 'react-redux';
import {
  aiAlerts1minPersistenceSelector,
  isAdminRoleSelector,
  ecoAIAlert2AccessSelector,
} from '@src/store/common/selectors';
import { checkErrors } from '../errorUtils';
import { SensitivityConfig } from '../interface';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import Highlight from '@src/components/common/Highlight';
import { ApartmentOutlined } from '@ant-design/icons';
import { ScanIconWrap } from '../../styled';

const SensitivityRow = ({ sensitivity, searchText, showAICohortButton, disableAICohortButton }) => {
  const selectedProduct = useAtomValue(selectedProductAtom);
  const [sensitivityDetails, setSensitivityDetails] = useAtom(fullSensitivityConfigAtom);
  const [errorMsg, setErrorMsg] = useState('');
  const setOpenAICohortDrawer = useSetAtom(aiCohortDrawerAtom);
  const is1minEnabled = useSelector(aiAlerts1minPersistenceSelector);
  const ecoAIAlert2 = useSelector(ecoAIAlert2AccessSelector);
  const minVal = is1minEnabled ? 1 : 3;
  const isAdminUser = useSelector(isAdminRoleSelector);
  const percentageMetric = getPercentageMetricByKey({
    metricKey: sensitivity.metricKey,
    defaultValue: sensitivity.metricKey,
  });

  // we show ImpactedDeviceFields when the AI ALERT 2.0 FF is off
  const showImpactedDeviceFields =
    !ecoAIAlert2 || ECO_AI_ALERT_NWR_METRICS.includes(sensitivity.metricKey);

  const showImpactedFlowsFields =
    ecoAIAlert2 && getMetricUnitByKey({ metricKey: percentageMetric }) === '%';

  const onChangeKey =
    ({ key }) =>
    (value) => {
      setSensitivityDetails((prev) => {
        return prev.map((sensitivityConfig: SensitivityConfig) => {
          if (sensitivityConfig.metricKey === sensitivity.metricKey) {
            return { ...sensitivityConfig, [key]: value };
          }
          return sensitivityConfig;
        });
      });
    };

  useEffect(() => {
    if (sensitivityDetails && sensitivity) {
      const sensitivityConfig = sensitivityDetails.find(
        (m: SensitivityConfig) => m.metricKey === sensitivity.metricKey,
      );
      if (sensitivityConfig) {
        setErrorMsg(checkErrors(sensitivityConfig, ecoAIAlert2));
      }
    }
  }, [sensitivityDetails, sensitivity]);

  const metricLabel = getMetricDisplayName({
    metricKey: sensitivity.metricKey,
    context: selectedProduct,
  });

  const handleAICohortClick = (e) => {
    e.stopPropagation(); // Prevent any parent element click handlers
    setOpenAICohortDrawer({
      open: true,
      metrics: [sensitivity.metricKey],
      key: sensitivity.metricKey,
      isFlow: false,
    });
  };

  return (
    <>
      <StyledDivider orientation="left" orientationMargin="0">
        <StyledTitle>
          <span>
            {searchText ? <Highlight text={metricLabel} highlight={searchText} /> : metricLabel}
          </span>
          {
            /* AI Cohort Metric Button - darker, bolder, more prominent for metric-level actions */
            showAICohortButton ? (
              <Tooltip
                title={
                  disableAICohortButton
                    ? 'Please save sensitivity to access AI Cohorts'
                    : 'Go to AI Cohorts'
                }
              >
                <Button
                  onClick={handleAICohortClick}
                  disabled={disableAICohortButton}
                  type="primary"
                  ghost
                  style={{ border: 'none', padding: '0 10px' }}
                >
                  <ScanIconWrap>
                    <ApartmentOutlined />
                  </ScanIconWrap>
                </Button>
              </Tooltip>
            ) : null
          }
        </StyledTitle>
      </StyledDivider>
      <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 16]}>
        <Col span={9}>
          <StyledConfigLabel>Persistence exceeds</StyledConfigLabel>
        </Col>

        <Col span={5}>
          <StyledInputWrapper>
            <StyledInput
              disabled={!isAdminUser}
              min={minVal}
              max={60}
              onChange={onChangeKey({ key: 'sustain' })}
              value={sensitivity.sustain}
            />
            <StyledUnit>min</StyledUnit>
          </StyledInputWrapper>
        </Col>

        <Col span={5}>
          <StyledInputWrapper>
            <StyledInput
              disabled={!isAdminUser}
              min={minVal}
              max={60}
              onChange={onChangeKey({ key: 'warningSustain' })}
              value={sensitivity.warningSustain}
            />
            <StyledUnit>min</StyledUnit>
          </StyledInputWrapper>
        </Col>

        <Col span={5}>
          <StyledInputWrapper>
            <StyledInput
              disabled={!isAdminUser}
              min={minVal}
              max={60}
              onChange={onChangeKey({ key: 'criticalSustain' })}
              value={sensitivity.criticalSustain}
            />
            <StyledUnit>min</StyledUnit>
          </StyledInputWrapper>
        </Col>
      </Row>
      {showImpactedDeviceFields ? (
        <ImpactedFieldsWrapper>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledConfigLabel>Total Impacted Devices exceeds</StyledConfigLabel>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={5}
                  max={1000000}
                  onChange={onChangeKey({ key: 'impactedUnique' })}
                  value={sensitivity.impactedUnique}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={5}
                  max={1000000}
                  onChange={onChangeKey({ key: 'warningImpactedUnique' })}
                  value={sensitivity.warningImpactedUnique}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={5}
                  max={1000000}
                  onChange={onChangeKey({ key: 'criticalImpactedUnique' })}
                  value={sensitivity.criticalImpactedUnique}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledConfigLabel>% Impacted Devices of Root Cause exceeds</StyledConfigLabel>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'impactedUniquePct' })}
                  value={sensitivity.impactedUniquePct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'warningImpactedUniquePct' })}
                  value={sensitivity.warningImpactedUniquePct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'criticalImpactedUniquePct' })}
                  value={sensitivity.criticalImpactedUniquePct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
        </ImpactedFieldsWrapper>
      ) : showImpactedFlowsFields ? (
        <ImpactedFieldsWrapper>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledConfigLabel>Incremental Impacted Count exceeds</StyledConfigLabel>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={1000000}
                  onChange={onChangeKey({ key: 'impactedFlows' })}
                  value={sensitivity.impactedFlows}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={1000000}
                  onChange={onChangeKey({ key: 'warningImpactedFlows' })}
                  value={sensitivity.warningImpactedFlows}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={1000000}
                  onChange={onChangeKey({ key: 'criticalImpactedFlows' })}
                  value={sensitivity.criticalImpactedFlows}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledConfigLabel>
                % Incremental Impacted Count of Root Cause exceeds
              </StyledConfigLabel>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'impactedFlowsPct' })}
                  value={sensitivity.impactedFlowsPct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'warningImpactedFlowsPct' })}
                  value={sensitivity.warningImpactedFlowsPct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'criticalImpactedFlowsPct' })}
                  value={sensitivity.criticalImpactedFlowsPct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
        </ImpactedFieldsWrapper>
      ) : null}
      {errorMsg ? <Alert message={errorMsg} type={'error'} /> : null}
    </>
  );
};

export default React.memo(SensitivityRow);
